<template>
  <v-card
    v-if="current_ci != null"
  >
    <v-card-title
      class="pr-2"
    >
      {{ current_ci.name }}
      <v-spacer />
      <v-btn
        rounded
        x-small
        icon
        title="Schließen"
        @click="$emit('close')"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-subtitle class="pt-1 d-flex align-center">
      <v-chip
        v-if="ciTypeMeta"
        small
        label
        outlined
      >
        <v-icon left small>{{ ciTypeMeta.icon }}</v-icon>
        {{ ciTypeMeta.label }}
      </v-chip>
      <span v-else>{{ current_ci.type }}</span>
      <v-chip
        v-if="current_ci && current_ci.status"
        small
        label
        :color="ciStatusMeta.color"
        text-color="white"
        class="ml-2"
      >
        <v-icon left small>{{ ciStatusMeta.icon }}</v-icon>
        {{ ciStatusMeta.label }}
      </v-chip>
      <v-spacer />
      <v-switch
        v-model="hideChildren"
        dense
        hide-details
        inset
        class="ma-0 mr-3 pa-0 subtitle-switch"
        label="Untergeordnete CIs ausblenden"
      />
      <v-switch
        v-model="hideDeleted"
        dense
        hide-details
        inset
        class="ma-0 pa-0 subtitle-switch"
        label="Gelöschte ausblenden"
      />
    </v-card-subtitle>
    <v-divider />

    <div class="chat-stack">
    <div ref="scroll" class="chat-scroll px-4 py-3">
      <div v-if="timeline.length === 0" class="text-caption grey--text text-center py-6">
        Noch keine Einträge im Logbuch.
      </div>

      <div
        v-for="event in timeline"
        :key="event.id"
        class="chat-row log-entry"
        :class="{ 'chat-row-own': is_own(event) }"
      >
        <div
          class="chat-bubble"
          :class="{ 'chat-bubble-own': is_own(event), 'chat-bubble-deleted': event.deleted }"
        >
          <div class="chat-meta">
            <span class="chat-author">{{ owner_names(event.owners) }}</span>
            <span class="chat-time" :title="formatFullTimestamp(event.timestamp)">
              {{ formatTimestamp(event.timestamp) }}
            </span>
            <v-tooltip v-if="hasDifferentCreated(event)" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon x-small class="ml-1 grey--text" v-bind="attrs" v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              <span>Eingetragen am {{ formatFullTimestamp(event.created_timestamp) }}</span>
            </v-tooltip>
            <v-spacer />
            <v-btn
              icon
              x-small
              class="ml-1 log-action"
              :title="event.deleted ? 'Wiederherstellen' : 'Löschen'"
              @click="toggle_deleted(event)"
            >
              <v-icon x-small>{{ event.deleted ? 'mdi-restore' : 'mdi-delete-outline' }}</v-icon>
            </v-btn>
          </div>

          <div class="chat-text log-message">{{ event.message }}</div>

          <div
            v-if="(event.ci_id !== current_ci_id) || (event.ticket_id && event.ticket_id !== -1)"
            class="d-flex align-center mt-1"
            style="gap: 4px;"
          >
            <v-chip
              v-if="event.ci_id !== current_ci_id"
              x-small
              label
              class="child-ci-chip"
              :title="'Zu „' + ci_name(event.ci_id) + '“ wechseln'"
              @click="$emit('switch-ci', event.ci_id)"
            >
              <v-icon x-small left class="child-ci-chip__icon">mdi-subdirectory-arrow-right</v-icon>
              {{ ci_name(event.ci_id) }}
            </v-chip>
            <v-chip
              v-if="event.ticket_id && event.ticket_id !== -1"
              x-small
              label
              disabled
              class="ticket-chip"
            >
              Ticket #{{ event.ticket_id }}
            </v-chip>
          </div>
        </div>
      </div>
    </div>

    <v-divider />

    <div class="pa-3">
      <v-text-field
        v-model="new_log.message"
        hide-details="auto"
        dense
        outlined
        placeholder="Kommentar hinterlassen…"
        class="comment-input"
        @keydown.enter="create_log"
      >
        <template v-slot:prepend-inner>
          <v-menu
            v-model="timestampMenu"
            :close-on-content-click="false"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                :title="hasCustomTimestamp ? 'Zeitpunkt: ' + customTimestampLabel : 'Zeitpunkt setzen (sonst jetzt)'"
              >
                <v-icon small :color="hasCustomTimestamp ? 'primary' : ''">
                  {{ hasCustomTimestamp ? 'mdi-clock-edit-outline' : 'mdi-clock-outline' }}
                </v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-text class="d-flex flex-wrap pa-2" style="gap: 8px;">
                <v-date-picker
                  v-model="logDate"
                  locale="de"
                  first-day-of-week="1"
                  no-title
                  scrollable
                />
                <v-time-picker
                  v-model="logTime"
                  format="24hr"
                  scrollable
                />
              </v-card-text>
              <v-card-actions>
                <v-btn text small @click="resetTimestamp">Auf jetzt</v-btn>
                <v-spacer />
                <v-btn text small color="primary" @click="timestampMenu = false">Schließen</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </template>
        <template v-slot:append>
          <v-btn
            icon
            small
            color="primary"
            :disabled="!canSend"
            :title="canSend ? 'Senden (Enter)' : ''"
            @click="create_log"
          >
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </template>
      </v-text-field>
      <div
        v-if="hasCustomTimestamp"
        class="text-caption grey--text text--darken-1 mt-1 ml-1 d-flex align-center"
      >
        <v-icon x-small class="mr-1">mdi-clock-edit-outline</v-icon>
        Zeitpunkt: {{ customTimestampLabel }}
        <v-btn x-small text class="ml-1" @click="resetTimestamp">zurücksetzen</v-btn>
      </div>
    </div>
    </div>
  </v-card>

</template>

<script>

import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import dayjs from "dayjs";
import { getCITypeMeta, getCIStatusMeta } from "@/utils/ci_meta"

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: "CIHistory",
  props: ["current_ci_id"],
  data: () => ({
    events: [],
    input: null,
    nonce: 0,

    timestampMenu: false,
    logDate: null,
    logTime: null,
    hideDeleted: false,
    hideChildren: false,
    fetched_log_ci_ids: new Set(),

    new_log: {
      ci_id: -1,
      owners: [],
      loglevel: 1,
      message: "",
      ticket_id: null,
    }
  }),
  watch: {
    descendantIds: {
      handler(ids){
        this.fetchLogsFor(ids)
      },
      immediate: true,
    },
    "timeline.length"() {
      this.scrollToTop()
    },
    current_ci_id() {
      this.scrollToTop()
    },
  },
  mounted() {
    this.scrollToTop()
  },
  computed: {
    current_ci(){
      if(this.current_ci_id !== null){
        return this.$store.getters.get_ci(this.current_ci_id)
      }
      return null
    },
    descendantIds () {
      const visited = new Set()
      if (this.current_ci_id == null) return []
      const visit = (id) => {
        if (visited.has(id)) return
        visited.add(id)
        const ci = this.$store.getters.get_ci(id)
        if (ci && Array.isArray(ci.children)) ci.children.forEach(visit)
      }
      visit(this.current_ci_id)
      return [...visited]
    },
    timeline () {
      if (this.current_ci_id == null) return []
      const lut = this.$store.state.ticketsystem.ci_logs_by_ci
      const ids = this.hideChildren ? [this.current_ci_id] : this.descendantIds
      const all = []
      for (const id of ids) {
        if (id in lut) all.push(...lut[id])
      }
      return all
        .filter(e => !this.hideDeleted || !e.deleted)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    },
    canSend () {
      return !!this.new_log.message && this.new_log.message.trim().length > 0
    },
    hasCustomTimestamp () {
      return !!(this.logDate || this.logTime)
    },
    customTimestampISO () {
      if (!this.hasCustomTimestamp) return null
      const date = this.logDate || dayjs().tz('Europe/Berlin').format('YYYY-MM-DD')
      const time = this.logTime || dayjs().tz('Europe/Berlin').format('HH:mm')
      return dayjs.tz(date + ' ' + time, 'Europe/Berlin').toISOString()
    },
    customTimestampLabel () {
      if (!this.hasCustomTimestamp) return ''
      return dayjs(this.customTimestampISO).tz('Europe/Berlin').format('DD.MM.YYYY, HH:mm [Uhr]')
    },
    ciTypeMeta () {
      return getCITypeMeta(this.current_ci && this.current_ci.type)
    },
    ciStatusMeta () {
      return getCIStatusMeta(this.current_ci && this.current_ci.status)
    },
  },
  methods: {
    ci_name(id){
      const ci = this.$store.getters.get_ci(id)
      return ci ? ci.name : '#' + id
    },
    is_own(event){
      const me = this.$store.state.user.id
      return me != null && Array.isArray(event.owners) && event.owners.includes(me)
    },
    owner_names(owners){
      if (!owners || owners.length === 0) return 'System'
      const users = this.$store.state.ticketsystem.users
      return owners.map(id => {
        const u = users.find(x => x.id === id)
        return u ? u.displayName : ('#' + id)
      }).join(', ')
    },
    scrollToTop(){
      this.$nextTick(() => {
        const el = this.$refs.scroll
        if (el) el.scrollTop = 0
      })
    },
    fetchLogsFor(ids){
      for (const id of ids) {
        if (this.fetched_log_ci_ids.has(id)) continue
        this.fetched_log_ci_ids.add(id)
        this.$store.dispatch("update", {
          payload_type: "GET",
          context: '/cis/' + id + '/log',
        })
      }
    },
    toggle_deleted(event){
      const next = !event.deleted
      this.$store.dispatch("update", {
        payload_type: "PUT",
        context: '/cis/log/' + event.id + '/deleted/' + (next ? 'true' : 'false'),
      })
    },
    resetTimestamp(){
      this.logDate = null
      this.logTime = null
    },
    hasDifferentCreated(event){
      if (!event.created_timestamp || !event.timestamp) return false
      return Math.abs(dayjs(event.created_timestamp).valueOf() - dayjs(event.timestamp).valueOf()) > 60_000
    },
    create_log(){
      if (!this.canSend) return
      if(this.$store.state.user){
        this.new_log.owners = [this.$store.state.user.id]
      }
      this.new_log.ci_id = this.current_ci_id
      const payload = { ...this.new_log, timestamp: this.customTimestampISO }
      this.$store.dispatch("update", { payload_type: "POST", context: '/cis/' + this.current_ci_id + "/log", payload})
      this.new_log.message = ""
      this.resetTimestamp()
    },
    formatTimestamp(ts) {
      const date = dayjs(ts).tz('Europe/Berlin')
      const now = dayjs().tz('Europe/Berlin')

      if (now.isSame(date, 'day')) return 'Heute, ' + date.format('HH:mm')
      if (now.subtract(1, 'day').isSame(date, 'day')) return 'Gestern, ' + date.format('HH:mm')
      if (now.isSame(date, 'year')) return date.format('DD.MM., HH:mm')
      return date.format('DD.MM.YYYY, HH:mm')
    },
    formatFullTimestamp(ts) {
      return dayjs(ts).tz('Europe/Berlin').format('DD.MM.YYYY, HH:mm:ss [Uhr]')
    },
    comment () {
      const time = (new Date()).toTimeString()
      this.events.push({
        id: this.nonce++,
        text: this.input,
        time: time.replace(/:\d{2}\sGMT-\d{4}\s\((.*)\)/, (match, contents) => {
          return ` ${contents.split(' ').map(v => v.charAt(0)).join('')}`
        }),
      })

      this.input = null
    },
  },
};
</script>

<style scoped>
.comment-input >>> .v-input__append-inner {
  margin-top: 4px !important;
  align-self: center;
}
.comment-input >>> fieldset {
  border-radius: 8px;
}

/* Chat-Optik (analog TicketChat) */
/* Eingabe oben, neueste Einträge direkt darunter ("auf den Stapel drauflegen") */
.chat-stack {
  display: flex;
  flex-direction: column-reverse;
}
.chat-scroll {
  overflow-y: auto;
  min-height: 240px;
  max-height: 60vh;
  background: rgba(0, 0, 0, 0.015);
}
.chat-row {
  display: flex;
  margin-bottom: 8px;
}
.chat-row-own {
  justify-content: flex-end;
}
.chat-bubble {
  max-width: 82%;
  min-width: 200px;
  padding: 6px 10px;
  border-radius: 12px;
  border-top-left-radius: 4px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.chat-bubble-own {
  background: rgba(123, 0, 0, 0.07);
  border-color: rgba(123, 0, 0, 0.25);
  border-top-left-radius: 12px;
  border-top-right-radius: 4px;
}
.chat-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}
.chat-author {
  font-size: 0.75rem;
  font-weight: 600;
  color: #7b0000;
}
.chat-time {
  font-size: 0.7rem;
  color: rgba(0, 0, 0, 0.5);
}
.chat-text {
  font-size: 0.9rem;
  white-space: pre-wrap;
  word-break: break-word;
}
.log-action {
  opacity: 0;
  transition: opacity 120ms ease-in-out;
}
.chat-row:hover .log-action {
  opacity: 1;
}
.chat-bubble-deleted {
  opacity: 0.7;
}
.chat-bubble-deleted .log-message {
  text-decoration: line-through;
  color: rgba(0, 0, 0, 0.38);
  font-weight: 400;
}
.subtitle-switch >>> .v-label {
  font-size: 0.75rem;
}
.child-ci-chip.v-chip {
  height: 20px !important;
  font-size: 0.7rem;
  letter-spacing: 0.2px;
  background: rgba(123, 0, 0, 0.08) !important;
  color: #7b0000 !important;
  border: none;
  padding: 0 8px;
  cursor: pointer;
  transition: background 120ms ease-in-out;
}
.child-ci-chip.v-chip:hover {
  background: rgba(123, 0, 0, 0.16) !important;
}
.child-ci-chip__icon {
  color: #7b0000 !important;
  opacity: 0.7;
  margin-right: 2px !important;
}
</style>
